import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Trash2, Video, Plus } from "lucide-react";

const empty = { video_id: null, title: "", url: "", category: "Général" };

export function AdminVideos() {
  const { t, tl } = useI18n();
  const [videos, setVideos] = useState([]);
  const [f, setF] = useState(empty);

  const load = () => api.get("/videos").then(({ data }) => setVideos(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.title || !f.url) return;
    try {
      await api.post("/admin/videos", f);
      toast.success(t("common.success"));
      setF(empty); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/videos/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const cats = useMemo(() => [...new Set(videos.map((v) => v.category || "Général"))], [videos]);
  const byCat = useMemo(() => {
    const m = {};
    videos.forEach((v) => { const c = v.category || "Général"; (m[c] ||= []).push(v); });
    return m;
  }, [videos]);

  return (
    <AdminLayout>
      <h1 className="mb-6 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
        <Video className="h-7 w-7 text-primary" /> {tl({ es: "Videoteca", en: "Video library", fr: "Vidéothèque" })}
      </h1>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-lg font-semibold">{tl({ es: "Añadir video", en: "Add video", fr: "Ajouter une vidéo" })}</h2>
          <div className="space-y-3">
            <div><Label>{tl({ es: "Título", en: "Title", fr: "Titre" })}</Label><Input data-testid="video-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1.5 h-11" /></div>
            <div><Label>{tl({ es: "Enlace (YouTube/Vimeo)", en: "Link (YouTube/Vimeo)", fr: "Lien (YouTube/Vimeo)" })}</Label><Input data-testid="video-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://youtu.be/..." className="mt-1.5 h-11" /></div>
            <div><Label>{tl({ es: "Categoría", en: "Category", fr: "Catégorie" })}</Label><Input data-testid="video-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Haltéro, Gym, Mobilité..." className="mt-1.5 h-11" /></div>
            <Button data-testid="video-save" onClick={save} disabled={!f.title || !f.url} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" />{f.video_id ? t("common.save") : tl({ es: "Añadir", en: "Add", fr: "Ajouter" })}</Button>
            {f.video_id && <Button variant="ghost" onClick={() => setF(empty)} className="rounded-full">{tl({ es: "Nuevo", en: "New", fr: "Nouveau" })}</Button>}
          </div>
        </div>

        <div className="lg:col-span-2">
          {videos.length === 0 && <p className="text-sm text-muted-foreground">{t("common.noData")}</p>}
          {cats.map((cat) => (
            <div key={cat} className="mb-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{cat}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                {byCat[cat].map((v) => (
                  <div key={v.video_id} data-testid={`video-card-${v.video_id}`} className="overflow-hidden rounded-xl border border-border bg-card">
                    {(v.provider === "youtube" || v.provider === "vimeo") ? (
                      <div className="aspect-video w-full bg-black"><iframe src={v.embed_url} title={v.title} className="h-full w-full" allowFullScreen /></div>
                    ) : (
                      <a href={v.url} target="_blank" rel="noreferrer" className="block p-4 text-sm text-primary underline">{v.url}</a>
                    )}
                    <div className="flex items-center justify-between p-3">
                      <span className="truncate text-sm font-medium">{v.title}</span>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" data-testid={`edit-video-${v.video_id}`} onClick={() => setF({ video_id: v.video_id, title: v.title, url: v.url, category: v.category })} className="rounded-full">{tl({ es: "Editar", en: "Edit", fr: "Modifier" })}</Button>
                        <Button size="sm" variant="ghost" data-testid={`del-video-${v.video_id}`} onClick={() => del(v.video_id)} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
